import { Auth } from 'aws-amplify';
import getConfig from 'next/config';
import ReconnectingWebSocket from 'reconnecting-websocket';
import { INotification, IUser } from 'stores/networkInterfaces';
import { store } from 'stores/store';
import {
  getGroups,
  getInvestorId,
  getUserId,
  IAuthDetails,
  isAuthConfigured,
  isInvestorVerified,
  isManagementUser,
  isSystemUser
} from 'utils/authUtils';

const { publicRuntimeConfig } = getConfig();
const { NEXT_PUBLIC_DOMAIN, NEXT_PUBLIC_WS_URL } = publicRuntimeConfig;
const investorWSUrl = NEXT_PUBLIC_WS_URL ?? 'wss://investor.ws.' + NEXT_PUBLIC_DOMAIN;

let rws: ReconnectingWebSocket | undefined;

const getAuthDetails = async (
  authToken?: string,
  groups?: string[],
  userId?: string,
  investorId?: string,
  investorVerified?: boolean
) => {
  try {
    if (authToken == undefined) {
      if (isAuthConfigured()) {
        const session = await Auth.currentSession();
        userId = getUserId(session);
        investorId = getInvestorId(session);
        investorVerified = isInvestorVerified(session);
        groups = getGroups(session);
        authToken = session.getIdToken().getJwtToken();
      }
    }
  } catch (e) {
    // ignore exception
  }

  return {
    isAuthenticated: authToken != undefined,
    authToken,
    groups,
    userId,
    investorId,
    investorVerified,
    isSystem: isSystemUser(groups),
    isManagement: isManagementUser(groups)
  };
};

const getIdentity = (authDetails: IAuthDetails): string[] | undefined => {
  if (!authDetails?.userId) {
    return;
  }

  if (authDetails?.isSystem) {
    return [authDetails?.userId, 'system'];
  }

  if (authDetails?.isManagement) {
    if (authDetails?.groups) {
      return [
        authDetails?.userId,
        ...authDetails?.groups
          ?.filter((group) => group.startsWith('sponsor_'))
          ?.map((group) => group.split('_')[1])
      ];
    }
  }

  if (authDetails?.investorId) {
    return [authDetails?.userId, authDetails?.investorId];
  }

  return [authDetails?.userId];
};

const wsUrlProvider = async () => {
  const authDetails = await getAuthDetails();
  if (!authDetails?.authToken) {
    return investorWSUrl;
  }

  const identity = getIdentity(authDetails);
  if (!identity) {
    return investorWSUrl;
  }

  return (
    `${investorWSUrl}` +
    `?Identity=${encodeURIComponent(identity.join(','))}` +
    `&Auth=${encodeURIComponent(authDetails.authToken)}`
  );
};

const wsShouldConnect = (authDetails?: IAuthDetails) => {
  return authDetails?.isAuthenticated;
};

const createSocket = (authDetails?: IAuthDetails) => {
  if (!rws && investorWSUrl) {
    rws = new ReconnectingWebSocket(wsUrlProvider, [], { startClosed: true });

    rws.addEventListener('message', function (event) {
      try {
        if (event?.data) {
          const data = JSON.parse(event.data);
          if (data?.data?.type == 'Notification') {
            const notification = data?.data?.attributes as INotification;
            console.log('Notification Websocket message', notification);

            // list notifications
            store.NotificationStore.listDirectNotifications({ filter: 'unread' });

            // list watch notifications
            if (!authDetails?.isSystem && !authDetails?.isManagement) {
              store.NotificationStore.listWatchNotifications({ filter: 'unread' });
            }

            if (notification?.order_id) {
              store.NotificationStore.updateLastOrderChange(notification?.order_id);
            }

            if (notification?.trade_id) {
              store.NotificationStore.updateLastTradeChange(notification?.trade_id);
            }
          } else if (data?.data?.type == 'User') {
            const user = data?.data?.attributes as IUser;
            console.log('User Websocket message', user);

            // fetch profile
            store.ProfileStore.fetchProfile();
          } else {
            console.log('Unsupported Websocket message', event.data);
          }
        }
      } catch (err) {
        console.log('Invalid Websocket message', event.data, err);
      }
    });
  }
};

const connectSocket = () => {
  if (rws) {
    rws.reconnect();
  }
};

const closeSocket = () => {
  if (rws) {
    rws.close();
    rws = undefined;
  }
};

export const connectWS = async (authDetails?: IAuthDetails) => {
  createSocket(authDetails);

  if (wsShouldConnect(authDetails)) {
    connectSocket();
  } else {
    closeSocket();
  }
};
